/**
 * Tarayıcıdaki kalıcı dosya deposu (IndexedDB). Masaüstünde kayıtlar
 * Belgeler/TalkNote klasörüne yazılıyor; web'de buraya:
 *
 *  - `files`: bitmiş kayıtlar ve yüklenen ses dosyaları, buluta arşivlenene
 *    kadar. Masaüstündeki dosya yolunun karşılığı `idb:<kimlik>`.
 *  - `chunks`: süren kaydın parçaları (~10 sn'de bir). Sekme kapanır ya da
 *    tarayıcı çökerse kayıt bunlardan kurtarılıyor (bkz. recoverRecordings).
 */

export interface StoredFile {
  id: string;
  blob: Blob;
  name: string;
  kind: 'recording' | 'import';
  createdAt: number;
  /** Sunucuda mp3'e çevrilmiş kopyanın yükleme kimliği (dosya yüklemede döküm sonrası). */
  serverUploadId?: string;
}

export interface StoredChunk {
  recordingId: string;
  seq: number;
  blob: Blob;
  at: number;
}

const DB_NAME = 'talknote';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('files')) db.createObjectStore('files', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('chunks')) {
        const chunks = db.createObjectStore('chunks', { keyPath: ['recordingId', 'seq'] });
        chunks.createIndex('byRecording', 'recordingId');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB açılamadı'));
    request.onblocked = () => reject(new Error('IndexedDB başka bir sekmede güncelleniyor'));
  }).catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB isteği başarısız'));
  });
}

async function store(name: 'files' | 'chunks', mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return (await open()).transaction(name, mode).objectStore(name);
}

export async function putFile(file: StoredFile): Promise<void> {
  await done((await store('files', 'readwrite')).put(file));
}

export async function getFile(id: string): Promise<StoredFile | null> {
  return ((await done((await store('files', 'readonly')).get(id))) as StoredFile | undefined) ?? null;
}

export async function updateFile(id: string, patch: Partial<Omit<StoredFile, 'id'>>): Promise<void> {
  const current = await getFile(id);
  if (current) await putFile({ ...current, ...patch });
}

export async function listFiles(): Promise<StoredFile[]> {
  return (await done((await store('files', 'readonly')).getAll())) as StoredFile[];
}

export async function deleteFile(id: string): Promise<void> {
  await done((await store('files', 'readwrite')).delete(id));
}

export async function putChunk(chunk: StoredChunk): Promise<void> {
  await done((await store('chunks', 'readwrite')).put(chunk));
}

/** Kaydın parçaları, sırasıyla. */
export async function readChunks(recordingId: string): Promise<StoredChunk[]> {
  const index = (await store('chunks', 'readonly')).index('byRecording');
  const chunks = (await done(index.getAll(IDBKeyRange.only(recordingId)))) as StoredChunk[];
  return chunks.sort((a, b) => a.seq - b.seq);
}

export async function deleteChunks(recordingId: string): Promise<void> {
  const index = (await store('chunks', 'readwrite')).index('byRecording');
  const keys = await done(index.getAllKeys(IDBKeyRange.only(recordingId)));
  const chunks = await store('chunks', 'readwrite');
  await Promise.all(keys.map((key) => done(chunks.delete(key))));
}

/** Parçası kalmış kayıtların kimlikleri (yarıda kalmış kayıtlar). */
export async function listChunkRecordings(): Promise<string[]> {
  const index = (await store('chunks', 'readonly')).index('byRecording');
  const keys = (await done(index.getAllKeys())) as Array<[string, number]>;
  return [...new Set(keys.map(([recordingId]) => recordingId))];
}
